import React, { useState, useEffect } from 'react';
import { 
  FaTimes, 
  FaTicketAlt, 
  FaUser, 
  FaEnvelope, 
  FaPhone, 
  FaCalendarAlt, 
  FaClock, 
  FaExclamationTriangle,
  FaCheckCircle,
  FaSpinner,
  FaReply,
  FaSave
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { supportService } from '../../api/supportService';

const SupportTicketModal = ({ isOpen, onClose, ticketId, onTicketUpdated }) => {
  const [ticket, setTicket] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [formData, setFormData] = useState({
    status: '',
    priority: '',
    assignedTo: '',
    adminNotes: '',
    resolution: ''
  });

  useEffect(() => {
    if (isOpen && ticketId) {
      fetchTicketDetails();
    }
  }, [isOpen, ticketId]);

  const fetchTicketDetails = async () => {
    try {
      setLoading(true);
      const response = await supportService.getTicketById(ticketId);
      
      if (response.success) {
        setTicket(response.data.ticket);
        setUser(response.data.user);
        setFormData({
          status: response.data.ticket.status || '',
          priority: response.data.ticket.priority || '',
          assignedTo: response.data.ticket.assignedTo || '',
          adminNotes: response.data.ticket.adminNotes || '',
          resolution: response.data.ticket.resolution || ''
        });
      } else {
        toast.error(response.message || 'Failed to load ticket details');
      }
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      toast.error('Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateTicket = async () => {
    try {
      setUpdating(true);
      const updateData = {};
      
      if (formData.status) updateData.status = formData.status;
      if (formData.priority) updateData.priority = formData.priority;
      if (formData.assignedTo) updateData.assignedTo = formData.assignedTo;
      if (formData.adminNotes) updateData.adminNotes = formData.adminNotes;

      const response = await supportService.updateTicket(ticketId, updateData);
      
      if (response.success) {
        toast.success('Ticket updated successfully');
        await fetchTicketDetails(); // Refresh ticket data
        if (onTicketUpdated) onTicketUpdated();
      } else {
        toast.error(response.message || 'Failed to update ticket');
      }
    } catch (error) {
      console.error('Error updating ticket:', error);
      toast.error('Failed to update ticket');
    } finally {
      setUpdating(false);
    }
  };

  const handleResolveTicket = async () => {
    if (!formData.resolution.trim()) {
      toast.error('Please provide resolution details');
      return;
    }

    try {
      setUpdating(true);
      const response = await supportService.resolveTicket(ticketId, {
        resolution: formData.resolution,
        adminNotes: formData.adminNotes
      });
      
      if (response.success) {
        toast.success('Ticket resolved successfully');
        await fetchTicketDetails(); // Refresh ticket data
        if (onTicketUpdated) onTicketUpdated();
      } else {
        toast.error(response.message || 'Failed to resolve ticket');
      }
    } catch (error) {
      console.error('Error resolving ticket:', error);
      toast.error('Failed to resolve ticket');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'OPEN': 'bg-red-100 text-red-800',
      'IN_PROGRESS': 'bg-yellow-100 text-yellow-800',
      'RESOLVED': 'bg-green-100 text-green-800',
      'CLOSED': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Urgent': 'bg-red-100 text-red-800',
      'High': 'bg-orange-100 text-orange-800',
      'Medium': 'bg-yellow-100 text-yellow-800',
      'Low': 'bg-green-100 text-green-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleString();
  };

  if (!isOpen || !ticketId) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <FaTicketAlt className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Support Ticket Details</h2>
              <p className="text-blue-100">Ticket #{ticketId}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-blue-200 transition-colors"
          >
            <FaTimes className="h-6 w-6" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <FaSpinner className="animate-spin h-8 w-8 text-blue-500" />
            <span className="ml-2 text-gray-600">Loading ticket details...</span>
          </div>
        ) : ticket ? (
          <div className="p-6 space-y-6">
            {/* Ticket Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Ticket Information</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">Subject</p>
                    <p className="font-medium text-gray-900">{ticket.subject || 'No subject'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Description</p>
                    <p className="text-gray-900">{ticket.description || 'No description'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Category</p>
                    <p className="font-medium text-gray-900">{ticket.category || 'General'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Created</p>
                    <p className="text-gray-900">{formatDate(ticket.createdAt)}</p>
                  </div>
                  {ticket.updatedAt && (
                    <div>
                      <p className="text-sm text-gray-600">Last Updated</p>
                      <p className="text-gray-900">{formatDate(ticket.updatedAt)}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Status & Priority</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(ticket.status)}`}>
                      {ticket.status || 'UNKNOWN'}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Priority</p>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(ticket.priority)}`}>
                      {ticket.priority || 'MEDIUM'}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Assigned To</p>
                    <p className="font-medium text-gray-900">{ticket.assignedTo || 'Unassigned'}</p>
                  </div>
                  {ticket.resolvedAt && (
                    <div>
                      <p className="text-sm text-gray-600">Resolved At</p>
                      <p className="text-gray-900">{formatDate(ticket.resolvedAt)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* User Information */}
            {user && (
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <FaUser className="mr-2 text-blue-600" />
                  User Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="font-medium text-gray-900">{user.firstName} {user.lastName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-medium text-gray-900">{user.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="font-medium text-gray-900">{user.phone || 'N/A'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Admin Actions */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Admin Actions</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Status</option>
                    <option value="OPEN">Open</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="RESOLVED">Resolved</option>
                    <option value="CLOSED">Closed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Priority</option>
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Assigned To</label>
                <input
                  type="text"
                  name="assignedTo"
                  value={formData.assignedTo}
                  onChange={handleInputChange}
                  placeholder="Admin name or ID"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Admin Notes</label>
                <textarea
                  name="adminNotes"
                  value={formData.adminNotes}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Internal notes for admin team..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {ticket.status !== 'RESOLVED' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Resolution Details</label>
                  <textarea
                    name="resolution"
                    value={formData.resolution}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="How was this issue resolved?"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={handleUpdateTicket}
                  disabled={updating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
                >
                  <FaSave className="mr-2" />
                  {updating ? 'Updating...' : 'Update Ticket'}
                </button>

                {ticket.status !== 'RESOLVED' && (
                  <button
                    onClick={handleResolveTicket}
                    disabled={updating || !formData.resolution.trim()}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center"
                  >
                    <FaCheckCircle className="mr-2" />
                    {updating ? 'Resolving...' : 'Resolve Ticket'}
                  </button>
                )}
              </div>
            </div>

            {/* Resolution History */}
            {ticket.resolution && (
              <div className="bg-green-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <FaCheckCircle className="mr-2 text-green-600" />
                  Resolution
                </h3>
                <p className="text-gray-900">{ticket.resolution}</p>
                {ticket.resolvedAt && (
                  <p className="text-sm text-gray-600 mt-2">
                    Resolved on {formatDate(ticket.resolvedAt)}
                  </p>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <FaTicketAlt className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-500">Ticket not found</p>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end p-6 border-t bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupportTicketModal;
